import React, { useState, useEffect, useCallback } from 'react';
import { Save } from 'lucide-react';
import { supabase } from '../../lib/supabase';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import Select from '../../components/ui/Select';
import { Label } from '../../components/ui/label';
import { Textarea } from '../../components/ui/textarea';
import Button from '../../components/ui/Button';
import DentalChartIso from './DentalChartIso';
import { toast } from 'sonner';
import { getDefaultAssistantPrescriptionForm } from '../../lib/defaultCasePrescription';

const archOptions = [
  { value: 'Upper', label: 'Arcade supérieure' },
  { value: 'Lower', label: 'Arcade inférieure' },
  { value: 'Both', label: 'Supérieure et inférieure' },
];

const treatmentTypeOptions = [
  { value: 'Full Arch', label: 'Arcade complète' },
  { value: 'Partial', label: 'Traitement partiel' },
  { value: 'Comprehensive', label: 'Traitement complet' },
  { value: 'Anterior', label: 'Antérieur uniquement' },
];

const improveOptions = [
  { value: 'Improve', label: 'Améliorer' },
  { value: 'Maintain', label: 'Maintenir' },
  { value: 'Correct', label: 'Corriger' },
];

const spacingOptions = [
  { value: 'Close All', label: 'Fermer tout' },
  { value: 'Maintain', label: 'Maintenir' },
  { value: 'Create', label: 'Créer' },
];

const yesNoOptions = [
  { value: 'Yes', label: 'Oui' },
  { value: 'No', label: 'Non' },
];

const iprOptions = [
  { value: 'Primarily', label: 'Principalement' },
  { value: 'As Needed', label: 'Si nécessaire' },
  { value: 'None', label: 'Aucun' },
];

const expandOptions = [
  { value: 'Primarily', label: 'Principalement' },
  { value: 'As Needed', label: 'Si nécessaire' },
  { value: 'None', label: 'Aucun' },
];

function normalizeArch(v) {
  if (!v) return '';
  const s = String(v);
  const x = s.toLowerCase();
  if (x === 'upper') return 'Upper';
  if (x === 'lower') return 'Lower';
  if (x === 'both') return 'Both';
  return s;
}

const emptyForm = getDefaultAssistantPrescriptionForm;

/**
 * Étape Prescription (assistant d’envoi) — mêmes champs que la fiche cas.
 * Mise en forme: sections en cartes, dents en grille 4 colonnes, IPR en bloc bleu clair.
 */
const AssistantPrescriptionForm = ({ caseId, onBack, onAfterSaveContinue }) => {
  const [formData, setFormData] = useState(emptyForm);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const load = useCallback(async () => {
    if (!caseId) return;
    setLoading(true);
    try {
      const { data: c, error: ce } = await supabase
        .from('cases')
        .select('arch_to_treat, treatment_type, questions_notes')
        .eq('id', caseId)
        .single();
      if (ce) throw ce;

      const { data: specs } = await supabase
        .from('case_treatment_specs')
        .select('*')
        .eq('case_id', caseId)
        .maybeSingle();

      const defaults = getDefaultAssistantPrescriptionForm();

      setFormData({
        ...defaults,
        archToTreat: normalizeArch(c?.arch_to_treat) || defaults.archToTreat,
        treatmentType: c?.treatment_type || defaults.treatmentType,
        questionsNotes: c?.questions_notes || '',
        overjet: specs?.overject ?? defaults.overjet,
        overbite: specs?.overbite ?? defaults.overbite,
        midline: specs?.midline ?? defaults.midline,
        posteriorCrossbite: specs?.posterior_crossbite ?? defaults.posteriorCrossbite,
        spacing: specs?.spacing ?? defaults.spacing,
        sequentialDistalization: specs?.sequential_distalization ?? defaults.sequentialDistalization,
        expand: specs?.expand ?? defaults.expand,
        procline: specs?.procline ?? defaults.procline,
        canine: specs?.canine ?? defaults.canine,
        molar: specs?.molar ?? defaults.molar,
        iprAnterior: specs?.ipr_anterior ?? defaults.iprAnterior,
        iprPosteriorRight: specs?.ipr_posterior_right ?? defaults.iprPosteriorRight,
        iprPosteriorLeft: specs?.ipr_posterior_left ?? defaults.iprPosteriorLeft,
        toothMovementRestrictions: specs?.tooth_mvt_restrictions ?? defaults.toothMovementRestrictions,
        attachmentRestrictions: specs?.attachment_restrictions ?? defaults.attachmentRestrictions,
        extractions: specs?.extractions ?? defaults.extractions,
        missingTeeth: specs?.missing_teeth ?? defaults.missingTeeth,
      });
    } catch (e) {
      console.error(e);
      toast.error('Impossible de charger la prescription : ' + (e?.message || e));
    } finally {
      setLoading(false);
    }
  }, [caseId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleInputChange = (field, value) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const toggleArrayValue = (field, value) => {
    setFormData((prev) => {
      const currentArray = prev[field] || [];
      const newArray = currentArray.includes(value)
        ? currentArray.filter((item) => item !== value)
        : [...currentArray, value];
      return { ...prev, [field]: newArray };
    });
  };

  const save = async () => {
    if (!caseId) return false;
    setSaving(true);
    try {
      const { error: caseError } = await supabase
        .from('cases')
        .update({
          arch_to_treat: formData.archToTreat ? formData.archToTreat.toLowerCase() : null,
          treatment_type: formData.treatmentType || null,
          questions_notes: formData.questionsNotes || null,
          updated_at: new Date().toISOString(),
        })
        .eq('id', caseId);
      if (caseError) throw caseError;

      const { error: specsError } = await supabase.from('case_treatment_specs').upsert(
        {
          case_id: caseId,
          overject: formData.overjet,
          overbite: formData.overbite,
          midline: formData.midline,
          posterior_crossbite: formData.posteriorCrossbite,
          spacing: formData.spacing,
          sequential_distalization: formData.sequentialDistalization,
          expand: formData.expand,
          procline: formData.procline,
          canine: formData.canine,
          molar: formData.molar,
          ipr_anterior: formData.iprAnterior,
          ipr_posterior_right: formData.iprPosteriorRight,
          ipr_posterior_left: formData.iprPosteriorLeft,
          tooth_mvt_restrictions: formData.toothMovementRestrictions,
          attachment_restrictions: formData.attachmentRestrictions,
          extractions: formData.extractions,
          missing_teeth: formData.missingTeeth,
          updated_at: new Date().toISOString(),
        },
        { onConflict: 'case_id' }
      );
      if (specsError) throw specsError;

      toast.success('Prescription enregistrée');
      return true;
    } catch (e) {
      console.error(e);
      toast.error("Enregistrement impossible : " + (e?.message || e));
      return false;
    } finally {
      setSaving(false);
    }
  };

  const handleDraft = async () => {
    await save();
  };

  const handleNext = async () => {
    const ok = await save();
    if (ok) onAfterSaveContinue?.();
  };

  if (loading) {
    return (
      <p className="text-sm text-muted-foreground py-6">Chargement de la prescription…</p>
    );
  }

  return (
    <div className="space-y-6">
      <p className="text-sm text-foreground">
        Renseignez la prescription d’alignement. Vous pourrez la modifier plus tard sur la fiche
        cas.
      </p>

      <Card className="border-border/80 shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Prescription d’alignement</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label>Arcade à traiter</Label>
            <Select
              options={archOptions}
              value={formData.archToTreat}
              onChange={(v) => handleInputChange('archToTreat', v)}
            />
          </div>
          <div className="space-y-2">
            <Label>Type de traitement</Label>
            <Select
              options={treatmentTypeOptions}
              value={formData.treatmentType}
              onChange={(v) => handleInputChange('treatmentType', v)}
            />
          </div>
          <div className="space-y-2">
            <Label>Overjet</Label>
            <Select
              options={improveOptions}
              value={formData.overjet}
              onChange={(v) => handleInputChange('overjet', v)}
            />
          </div>
          <div className="space-y-2">
            <Label>Overbite</Label>
            <Select
              options={improveOptions}
              value={formData.overbite}
              onChange={(v) => handleInputChange('overbite', v)}
            />
          </div>
          <div className="space-y-2">
            <Label>Ligne médiane</Label>
            <Select
              options={improveOptions}
              value={formData.midline}
              onChange={(v) => handleInputChange('midline', v)}
            />
          </div>
          <div className="space-y-2">
            <Label>Occlusion croisée postérieure</Label>
            <Select
              options={improveOptions}
              value={formData.posteriorCrossbite}
              onChange={(v) => handleInputChange('posteriorCrossbite', v)}
            />
          </div>
          <div className="space-y-2">
            <Label>Espacement</Label>
            <Select
              options={spacingOptions}
              value={formData.spacing}
              onChange={(v) => handleInputChange('spacing', v)}
            />
          </div>
          <div className="space-y-2">
            <Label>Distalisation séquentielle</Label>
            <Select
              options={yesNoOptions}
              value={formData.sequentialDistalization}
              onChange={(v) => handleInputChange('sequentialDistalization', v)}
            />
          </div>
        </CardContent>
      </Card>

      <Card className="border-border/80 shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Encombrement &amp; espacement</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-3">
            <Label className="text-sm font-medium">Encombrement (expansion)</Label>
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Haut</Label>
              <Select
                options={expandOptions}
                value={formData.expand.upper}
                onChange={(v) => handleInputChange('expand', { ...formData.expand, upper: v })}
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Bas</Label>
              <Select
                options={expandOptions}
                value={formData.expand.lower}
                onChange={(v) => handleInputChange('expand', { ...formData.expand, lower: v })}
              />
            </div>
          </div>
          <div className="space-y-3">
            <Label className="text-sm font-medium">Proclination</Label>
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Haut</Label>
              <Select
                options={expandOptions}
                value={formData.procline.upper}
                onChange={(v) => handleInputChange('procline', { ...formData.procline, upper: v })}
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Bas</Label>
              <Select
                options={expandOptions}
                value={formData.procline.lower}
                onChange={(v) => handleInputChange('procline', { ...formData.procline, lower: v })}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <Card className="border-border/80 shadow-sm">
        <CardHeader className="pb-3">
          <CardTitle className="text-base">Relation antéro-postérieure</CardTitle>
        </CardHeader>
        <CardContent className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div className="space-y-3">
            <Label className="text-sm font-medium">Canine</Label>
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Haut</Label>
              <Select
                options={improveOptions}
                value={formData.canine.upper}
                onChange={(v) => handleInputChange('canine', { ...formData.canine, upper: v })}
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Bas</Label>
              <Select
                options={improveOptions}
                value={formData.canine.lower}
                onChange={(v) => handleInputChange('canine', { ...formData.canine, lower: v })}
              />
            </div>
          </div>
          <div className="space-y-3">
            <Label className="text-sm font-medium">Molaire</Label>
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Haut</Label>
              <Select
                options={improveOptions}
                value={formData.molar.upper}
                onChange={(v) => handleInputChange('molar', { ...formData.molar, upper: v })}
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Bas</Label>
              <Select
                options={improveOptions}
                value={formData.molar.lower}
                onChange={(v) => handleInputChange('molar', { ...formData.molar, lower: v })}
              />
            </div>
          </div>
        </CardContent>
      </Card>

      <div className="rounded-xl border border-sky-200/90 bg-gradient-to-b from-sky-50/90 via-sky-50/40 to-white dark:border-slate-600 dark:from-slate-900/50 dark:via-slate-900/30 dark:to-card shadow-sm">
        <div className="p-4 sm:p-5 border-b border-sky-200/50 dark:border-slate-600">
          <h3 className="text-base font-semibold text-foreground">
            Réduction inter-proximale (IPR)
          </h3>
        </div>
        <div className="p-4 sm:p-5 grid grid-cols-1 md:grid-cols-3 gap-6">
          <div className="space-y-3">
            <Label className="text-sm font-medium">IPR antérieur</Label>
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Haut</Label>
              <Select
                options={iprOptions}
                value={formData.iprAnterior.upper}
                onChange={(v) => handleInputChange('iprAnterior', { ...formData.iprAnterior, upper: v })}
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Bas</Label>
              <Select
                options={iprOptions}
                value={formData.iprAnterior.lower}
                onChange={(v) => handleInputChange('iprAnterior', { ...formData.iprAnterior, lower: v })}
              />
            </div>
          </div>
          <div className="space-y-3">
            <Label className="text-sm font-medium">IPR post. droit</Label>
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Haut</Label>
              <Select
                options={iprOptions}
                value={formData.iprPosteriorRight.upper}
                onChange={(v) =>
                  handleInputChange('iprPosteriorRight', { ...formData.iprPosteriorRight, upper: v })
                }
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Bas</Label>
              <Select
                options={iprOptions}
                value={formData.iprPosteriorRight.lower}
                onChange={(v) =>
                  handleInputChange('iprPosteriorRight', { ...formData.iprPosteriorRight, lower: v })
                }
              />
            </div>
          </div>
          <div className="space-y-3">
            <Label className="text-sm font-medium">IPR post. gauche</Label>
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Haut</Label>
              <Select
                options={iprOptions}
                value={formData.iprPosteriorLeft.upper}
                onChange={(v) =>
                  handleInputChange('iprPosteriorLeft', { ...formData.iprPosteriorLeft, upper: v })
                }
              />
            </div>
            <div className="space-y-2">
              <Label className="text-xs text-muted-foreground">Bas</Label>
              <Select
                options={iprOptions}
                value={formData.iprPosteriorLeft.lower}
                onChange={(v) =>
                  handleInputChange('iprPosteriorLeft', { ...formData.iprPosteriorLeft, lower: v })
                }
              />
            </div>
          </div>
        </div>
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-2 gap-4">
        <Card className="border-border/80 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Restrictions de mouvement dentaire</CardTitle>
          </CardHeader>
          <CardContent>
            <DentalChartIso
              idPrefix="mvt"
              selectedUniversal={formData.toothMovementRestrictions}
              onToggle={(u) => toggleArrayValue('toothMovementRestrictions', u)}
            />
          </CardContent>
        </Card>

        <Card className="border-border/80 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Restrictions d’attachements</CardTitle>
          </CardHeader>
          <CardContent>
            <DentalChartIso
              idPrefix="att"
              selectedUniversal={formData.attachmentRestrictions}
              onToggle={(u) => toggleArrayValue('attachmentRestrictions', u)}
            />
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="border-border/80 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Extractions</CardTitle>
          </CardHeader>
          <CardContent>
            <DentalChartIso
              idPrefix="ex"
              selectedUniversal={formData.extractions}
              onToggle={(u) => toggleArrayValue('extractions', u)}
            />
          </CardContent>
        </Card>
        <Card className="border-border/80 shadow-sm">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm">Dents manquantes</CardTitle>
          </CardHeader>
          <CardContent>
            <DentalChartIso
              idPrefix="mi"
              selectedUniversal={formData.missingTeeth}
              onToggle={(u) => toggleArrayValue('missingTeeth', u)}
            />
          </CardContent>
        </Card>
      </div>

      <Card className="border-border/80 shadow-sm">
        <CardHeader className="pb-2">
          <CardTitle className="text-sm text-center w-full">Notes supplémentaires</CardTitle>
        </CardHeader>
        <CardContent>
          <Textarea
            placeholder="Ajoutez des notes supplémentaires pour ce traitement…"
            value={formData.questionsNotes}
            onChange={(e) => handleInputChange('questionsNotes', e.target.value)}
            rows={4}
            className="w-full"
          />
        </CardContent>
      </Card>

      <div className="flex flex-col-reverse sm:flex-row sm:flex-wrap gap-3 pt-2 border-t border-border/60">
        <Button
          type="button"
          variant="outline"
          onClick={onBack}
          disabled={saving}
          className="w-full sm:w-auto sm:min-w-[140px]"
        >
          Précédent
        </Button>
        <Button
          type="button"
          variant="outline"
          onClick={handleDraft}
          disabled={saving}
          className="w-full sm:flex-1 sm:max-w-xs inline-flex items-center justify-center gap-2"
        >
          <Save className="w-4 h-4 shrink-0" />
          <span>Enregistrer comme brouillon</span>
        </Button>
        <Button
          type="button"
          onClick={handleNext}
          disabled={saving}
          className="w-full sm:flex-1 sm:max-w-xs sm:ml-auto"
        >
          {saving ? 'Enregistrement…' : 'Suivant'}
        </Button>
      </div>
    </div>
  );
};

export default AssistantPrescriptionForm;
